/**
 * SoundEffects - ZzFX Sound Configurations
 * 8-bit for UI, Modern synthesized for game sounds
 * 
 * Updated with richer, more distinct sound profiles.
 */

import { zzfx } from 'zzfx';

// UI Sounds (Crisp, clean 8-bit blips)
export const uiSounds = {
    hover: {
        volume: 0.5,
        randomness: 0,
        frequency: 1800,
        attack: 0.01,
        sustain: 0,
        release: 0.05,
        shape: 1, // Triangle
        shapeCurve: 0,
        slide: 0,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0,
        decay: 0,
        tremolo: 0,
        filter: 0
    },
    click: {
        volume: 0.8,
        randomness: 0,
        frequency: 600,
        attack: 0,
        sustain: 0,
        release: 0.03,
        shape: 0,
        shapeCurve: 0,
        slide: 0,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0,
        decay: 0,
        tremolo: 0,
        filter: 0
    },
    back: {
        volume: 1,
        randomness: 0,
        frequency: 300,
        attack: 0.01,
        sustain: 0,
        release: 0.1,
        shape: 3, // Sawtooth
        shapeCurve: 0,
        slide: -100,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0,
        decay: 0,
        tremolo: 0,
        filter: 0
    },
    menuOpen: {
        volume: 1,
        randomness: 0,
        frequency: 400,
        attack: 0.1,
        sustain: 0.1,
        release: 0.3,
        shape: 0,
        shapeCurve: 0,
        slide: 200,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0,
        decay: 0,
        tremolo: 0,
        filter: 0
    }
};

// Game Sounds (Modern, weightier physics sounds)
export const gameSounds = {
    grab: {
        volume: 1.2,
        randomness: 0,
        frequency: 440,
        attack: 0.01,
        sustain: 0.1,
        release: 0.1,
        shape: 0, // Sine
        shapeCurve: 1,
        slide: 200,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 1,
        decay: 0.1,
        tremolo: 0,
        filter: 0
    },
    release: {
        volume: 1.2,
        randomness: 0,
        frequency: 440,
        attack: 0.01,
        sustain: 0,
        release: 0.1,
        shape: 3, // Sawtooth for "edge"
        shapeCurve: 0,
        slide: -200,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0,
        decay: 0.1,
        tremolo: 0,
        filter: 0
    },
    impact: {
        volume: 2,
        randomness: 0.1,
        frequency: 100,
        attack: 0,
        sustain: 0.05,
        release: 0.2,
        shape: 4, // Noise
        shapeCurve: 1,
        slide: -50,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 1.5,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0,
        decay: 0.1,
        tremolo: 0,
        filter: 1 // Low pass
    },
    throw: {
        volume: 1,
        randomness: 0,
        frequency: 300,
        attack: 0.05,
        sustain: 0,
        release: 0.2,
        shape: 0,
        shapeCurve: 0,
        slide: 150,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0.2,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0,
        decay: 0,
        tremolo: 0,
        filter: 0
    }
};

// Scoring Sounds (Rewarding, musical)
export const scoringSounds = {
    tJunction: {
        volume: 1.2,
        randomness: 0,
        frequency: 880, // A5
        attack: 0,
        sustain: 0.05,
        release: 0.2,
        shape: 0, // Sine - clean bell tone
        shapeCurve: 0,
        slide: 0,
        deltaSlide: 0,
        pitchJump: 1760, // Jump to A6
        pitchJumpTime: 0.05,
        repeatTime: 0,
        noise: 0,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0.5,
        decay: 0.1,
        tremolo: 0,
        filter: 0
    },
    scoreUp: {
        volume: 1,
        randomness: 0,
        frequency: 880, // A5
        attack: 0.01,
        sustain: 0,
        release: 0.1,
        shape: 0, // Sine
        shapeCurve: 0,
        slide: 0,
        deltaSlide: 0,
        pitchJump: 880, // Octave jump
        pitchJumpTime: 0.05,
        repeatTime: 0,
        noise: 0,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0,
        decay: 0,
        tremolo: 0,
        filter: 0
    }
};

// Game State Sounds (Atmospheric)
export const gameStateSounds = {
    gameStart: {
        volume: 1,
        randomness: 0,
        frequency: 220,
        attack: 0.05,
        sustain: 0.1,
        release: 0.3,
        shape: 0,
        shapeCurve: 0,
        slide: 440,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0.5,
        decay: 0.1,
        tremolo: 0,
        filter: 0
    },
    settling: {
        volume: 0.5,
        randomness: 0.1,
        frequency: 200,
        attack: 0,
        sustain: 0,
        release: 0.05,
        shape: 4, // Noise
        shapeCurve: 0,
        slide: 0,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0.5,
        modulation: 0,
        bitCrush: 0,
        delay: 0,
        sustainVolume: 0,
        decay: 0,
        tremolo: 0,
        filter: 0
    },
    gameOver: {
        volume: 1.5,
        randomness: 0,
        frequency: 300,
        attack: 0.1,
        sustain: 0.5,
        release: 2,
        shape: 3, // Sawtooth
        shapeCurve: 0,
        slide: -200, // Slide down
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0,
        modulation: 50, // Heavy modulation/dissonance
        bitCrush: 1, // Crush it
        delay: 0.2,
        sustainVolume: 0.5,
        decay: 1,
        tremolo: 0,
        filter: 0
    }
};

// Timer Warning
export const timerSounds = {
    warning: {
        volume: 1,
        randomness: 0,
        frequency: 600,
        attack: 0.01,
        sustain: 0.05,
        release: 0.1,
        shape: 1, // Triangle
        shapeCurve: 0,
        slide: 0,
        deltaSlide: 0,
        pitchJump: 0,
        pitchJumpTime: 0,
        repeatTime: 0,
        noise: 0,
        modulation: 50, // Alarm-like wobble
        bitCrush: 0,
        delay: 0,
        sustainVolume: 1,
        decay: 0.1,
        tremolo: 0,
        filter: 0
    }
};

// Helper function to play ZzFX sound
export function playZzFX(config: any): void {
    zzfx(
        config.volume || 1,
        config.randomness || 0,
        config.frequency || 440,
        config.attack || 0,
        config.sustain || 0,
        config.release || 0.1,
        config.shape || 0,
        config.shapeCurve || 1,
        config.slide || 0,
        config.deltaSlide || 0,
        config.pitchJump || 0,
        config.pitchJumpTime || 0,
        config.repeatTime || 0,
        config.noise || 0,
        config.modulation || 0,
        config.bitCrush || 0,
        config.delay || 0,
        config.sustainVolume || 0,
        config.decay || 0,
        config.tremolo || 0,
        config.filter || 0
    );
}
